import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { InfoWindow, Marker } from '@react-google-maps/api';
import { LinkContainer } from 'react-router-bootstrap';
import dangerIconNotification from '../Assets/Pins/red-pin-notification.png';
import dangerIcon from '../Assets/Pins/red-pin.png';
import goodIconNotification from '../Assets/Pins/green-pin-notification.png';
import goodIcon from '../Assets/Pins/green-pin.png';
import warningIconNotification from '../Assets/Pins/yellow-pin-notification.png';
import warningIcon from '../Assets/Pins/yellow-pin.png';
import grayIcon from '../Assets/Pins/gray-pin.png';
import grayIconNotification from '../Assets/Pins/gray-pin-notification.png';
import tealIcon from '../Assets/Pins/teal-pin.png';
import { bind } from '../Utils/Bind';
import { UserAgent } from '../Utils/UserAgent';
import { BsMap } from '@react-icons/all-files/bs/BsMap';
import { geocodeLatLng } from './Map';

export interface IDisplayMapMarkerProps {
    onDragEnd?(e: google.maps.MapMouseEvent): void;
    markerRef?(ref: Marker | null): void;
    readonly serialNumber: string;
    readonly lat: number;
    readonly lng: number;
    readonly fullAddress?: string;
    readonly address1?: string;
    readonly address2?: string;
    readonly city?: string;
    readonly stateOrProvince?: string;
    readonly country?: string;
    readonly postalCode?: string;
    readonly color?: 'teal' | 'danger' | 'warning' | 'good' | 'inactive';
    readonly notification?: boolean;
    readonly draggable?: boolean;
    readonly defaultWindowOpen?: boolean;
}

@observer
export class DisplayMapMarker extends React.Component<IDisplayMapMarkerProps> {
    @bind
    @action
    public async componentDidMount() {
        // This code can be removed in a future version of SmartVu after
        // all lat/lngs have been geocoded in the database
        const lat = this.props.lat;
        const lng = this.props.lng;

        if (lat && lng) {
            const geocoderResult = await geocodeLatLng(lat, lng);

            if (geocoderResult) this.updateLocation(lat, lng, geocoderResult);
        }

        if (this.props.defaultWindowOpen) {
            this.windowOpen = true;
        }
    }

    public render() {
        const { markerRef, lat, lng, draggable } =
            this.props;
        const query = `${lat},${lng}`;
        const href = UserAgent.appleios()
            ? `https://maps.apple.com/?q=${query}`
            : `https://maps.google.com/?q=${query}`;
        const latLng = new google.maps.LatLng(lat, lng);

        return (
            <Marker position={latLng} onClick={this.toggleWindow} ref={markerRef} icon={this.icon} draggable={draggable} onDragEnd={this.props.onDragEnd}>
                {this.windowOpen && (
                    <InfoWindow onCloseClick={this.toggleWindow}>
                        <div className="info-window">
                            {UserAgent.mobile() ? (
                                // Mobile view: show only serial number
                                <LinkContainer to={'/displays/' + this.props.serialNumber} className="py-0 me-3 btn btn-link">
                                    <a>{this.props.serialNumber}</a>
                                </LinkContainer>
                            ) : (
                                // Desktop view: full info
                                <div>
                                    <LinkContainer to={'/displays/' + this.props.serialNumber} className="p-0 btn btn-link">
                                        <a>{this.props.serialNumber}</a>
                                    </LinkContainer>
                                    <a href={href} target="_blank" rel="noopener noreferrer">
                                        <BsMap className="" /> &nbsp;
                                        {lat},{lng}
                                        <br />
                                        <span>
                            {this.streetAddress},{this.city}, {this.stateOrProvince}
                        </span>
                                    </a>
                                </div>
                            )}
                        </div>
                    </InfoWindow>
                )}

            </Marker>
        );
    }

    @bind
    @action
    public toggleWindow() {
        this.windowOpen = !this.windowOpen;
    }

    @computed
    private get icon() {
        const { notification } = this.props;
        switch (this.props.color) {
            case 'teal':
                return tealIcon;
            case 'good':
                if (notification) return goodIconNotification;
                return goodIcon;
            case 'danger':
                if (notification) return dangerIconNotification;
                return dangerIcon;
            case 'warning':
                if (notification) return warningIconNotification;
                return warningIcon;
            case 'inactive':
                if (notification) return grayIconNotification;
                return grayIcon;
        }

        return goodIcon;
    }

    @bind
    @action
    private updateLocation(lat: number, lng: number, geocoderResult: google.maps.GeocoderResult) {
        const addressComponents = geocoderResult.address_components;
        const streetAddress = addressComponents.find(a => a.types.includes('street_number'));
        const route = addressComponents.find(a => a.types.includes('route'));
        const city = addressComponents.find(a => a.types.includes('locality'));
        const stateOrProvince = addressComponents.find(a => a.types.includes('administrative_area_level_1'));
        const country = addressComponents.find(a => a.types.includes('country'));
        const postalCode = addressComponents.find(a => a.types.includes('postal_code'));

        let combinedAddress: string | undefined;

        if (route && streetAddress) combinedAddress = `${streetAddress.long_name} ${route.long_name}`;
        else if (route && !streetAddress) combinedAddress = route.long_name;
        else if (!route && streetAddress) combinedAddress = streetAddress.long_name;
        else combinedAddress = undefined;

        if (combinedAddress) this.streetAddress = combinedAddress;

        if (city) this.city = city.long_name;

        if (stateOrProvince) this.stateOrProvince = stateOrProvince.long_name;

        if (country) this.country = country.long_name;

        if (postalCode) this.postalCode = postalCode.long_name;
    }

    @observable
    public windowOpen: boolean = false;
    @observable
    private streetAddress: string = '';
    @observable
    private city: string = '';
    @observable
    private stateOrProvince: string = '';
    @observable
    private country: string = '';
    @observable
    private postalCode: string = '';
}
